import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const genres = [
  { id: 'all', name: 'All Genres' },
  { id: 'Action', name: 'Action' },
  { id: 'Adventure', name: 'Adventure' },
  { id: 'Animation', name: 'Animation' },
  { id: 'Comedy', name: 'Comedy' },
  { id: 'Crime', name: 'Crime' },
  { id: 'Documentary', name: 'Documentary' },
  { id: 'Drama', name: 'Drama' },
  { id: 'Fantasy', name: 'Fantasy' },
  { id: 'Horror', name: 'Horror' },
  { id: 'Mystery', name: 'Mystery' },
  { id: 'Romance', name: 'Romance' },
  { id: 'Science Fiction', name: 'Sci-Fi' },
  { id: 'Thriller', name: 'Thriller' },
  { id: 'Western', name: 'Western' },
];

const years = [
  { id: 'all', name: 'All Years' },
  { id: '2024', name: '2024' },
  { id: '2023', name: '2023' },
  { id: '2022', name: '2022' },
  { id: '2020s', name: '2020s' },
  { id: '2010s', name: '2010s' },
  { id: '2000s', name: '2000s' },
  { id: '1990s', name: '1990s' },
  { id: 'classic', name: 'Before 1990' },
];

const categories = [
  { id: 'all', name: 'All Categories' },
  { id: 'newReleases', name: 'New Releases' },
  { id: 'classics', name: 'Classics' },
  { id: 'cult', name: 'Cult Films' },
  { id: 'documentaries', name: 'Documentaries' },
  { id: 'hiddenGems', name: 'Hidden Gems' },
  { id: 'directors', name: 'Director Spotlights' },
];

const sortOptions = [
  { id: 'newest', name: 'Newest First' },
  { id: 'oldest', name: 'Oldest First' },
  { id: 'matrix_score', name: 'Matrix Score' },
  { id: 'title', name: 'Title A-Z' },
];

const FilterDropdown = ({ label, options, value, onChange }) => {
  const selectedOption = options.find(o => o.id === value) || options[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2.5 bg-[#121917] border border-[#52ea88]/15 hover:border-[#52ea88]/40 text-[#E6FFF6] text-sm transition-all outline-none">
        <span className="text-[#9FBFB4] text-xs uppercase tracking-wider mr-1">{label}:</span>
        <span>{selectedOption.name}</span>
        <ChevronDown className="w-4 h-4 text-[#9FBFB4]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#121917] border border-[#52ea88]/15 min-w-[180px] max-h-[300px] overflow-auto">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onClick={() => onChange(option.id)}
            className={`px-4 py-2.5 cursor-pointer hover:bg-[#52ea88]/5 ${
              value === option.id ? 'text-[#52ea88]' : 'text-[#E6FFF6]'
            }`}
          >
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SearchFilter = ({ 
  onSearch, 
  onFilterChange, 
  showCategories = true,
  showYears = true,
  showGenres = true,
  showSort = true,
  initialFilters = {}
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    genre: 'all',
    year: 'all',
    category: 'all',
    sort: 'newest',
    ...initialFilters
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) onSearch(searchQuery);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const resetFilters = {
      genre: 'all',
      year: 'all',
      category: 'all',
      sort: 'newest',
    };
    setFilters(resetFilters);
    setSearchQuery('');
    if (onFilterChange) onFilterChange(resetFilters);
    if (onSearch) onSearch('');
  };

  const hasActiveFilters = filters.genre !== 'all' || filters.year !== 'all' || 
                           filters.category !== 'all' || searchQuery;

  return (
    <div className="space-y-4 mb-8">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9FBFB4]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search movies by title..."
            className="w-full pl-12 pr-4 py-3 bg-[#121917] border border-[#52ea88]/15 focus:border-[#52ea88]/50 text-[#E6FFF6] placeholder-[#9FBFB4]/50 outline-none transition-colors"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 bg-[#52ea88] text-[#0B0F0E] font-medium hover:bg-[#7aff9e] transition-colors"
        >
          Search
        </button>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={`px-4 py-3 border transition-colors ${
            isExpanded 
              ? 'bg-[#52ea88]/10 border-[#52ea88]/50 text-[#52ea88]' 
              : 'bg-[#121917] border-[#52ea88]/15 text-[#E6FFF6] hover:border-[#52ea88]/40'
          }`}
        >
          <SlidersHorizontal className="w-5 h-5" />
        </button>
      </form>

      {/* Filter Bar */}
      {isExpanded && (
        <div className="flex flex-wrap items-center gap-3 p-4 bg-[#121917]/50 border border-[#52ea88]/10">
          {showGenres && (
            <FilterDropdown
              label="Genre"
              options={genres}
              value={filters.genre}
              onChange={(v) => handleFilterChange('genre', v)}
            />
          )}
          
          {showYears && (
            <FilterDropdown
              label="Year"
              options={years}
              value={filters.year}
              onChange={(v) => handleFilterChange('year', v)}
            />
          )}
          
          {showCategories && (
            <FilterDropdown
              label="Category"
              options={categories}
              value={filters.category}
              onChange={(v) => handleFilterChange('category', v)}
            />
          )}
          
          {showSort && (
            <FilterDropdown
              label="Sort"
              options={sortOptions}
              value={filters.sort}
              onChange={(v) => handleFilterChange('sort', v)}
            />
          )}

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-[#9FBFB4] hover:text-[#52ea88] transition-colors text-sm"
            >
              <X className="w-4 h-4" />
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchFilter;
